/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import cx from 'classnames';
import * as _ from 'lodash';
import type { VoidFunctionComponent } from 'react';

import { defaultSnippetsSort, type SnippetsQueryParams, type SnippetsSort } from '../../../../../shared/snippets';
import { useQuery } from '../../../../contexts/RouterContext';
import { Checkbox, IconAndNode, icons, SortingDropdown } from '../../../generic';
import FallbackImage from '../../../generic/FallbackImage';
import List from '../../../generic/List';
import PackageLabelsList from '../../../shared/packageLabels/PackageLabelsList';
import { useSnippetsByUrl } from '../hooks';
import type { SortingDirection } from '../../../generic/SortingDropdown';
import type { SnippetSelectionState } from '../hooks/useSnippetSelectionState';

import BulkSelectionBanner from './BulkSelectionBanner';
import IssueCounts from './IssueCounts';
import LoaderRow from './LoaderRow';

import styles from './index.module.less';

const snippetKindIcons = {
  snippet: icons.receipt,
  file: icons.fileOutline,
};

type Props = {
  userRevisionLocator: string;
  selectionState: SnippetSelectionState,
};

/**
 * Component which renders the table view of the snippets.
 */
const SnippetsTable: VoidFunctionComponent<Props> = ({
  userRevisionLocator,
  selectionState,
}) => {
  const [queryParams, setQuery] = useQuery<SnippetsQueryParams>();
  const { data: snippets, isLoading } = useSnippetsByUrl();

  // Check the URL params for a sorting value, otherwise use the default.
  const currentSort = queryParams?.sort ? queryParams.sort : defaultSnippetsSort;
  const setCurrentSort = (newSort?: SnippetsSort) => {
    setQuery({
      ...queryParams,
      sort: newSort,
      page: undefined,
    });
  };

  const getPackageSortingDirection = () => {
    if (currentSort === 'package_asc') {
      return 'asc';
    }
    if (currentSort === 'package_desc') {
      return 'desc';
    }
    return undefined;
  };

  const setPackageSortingDirection = (direction?: SortingDirection) => {
    if (direction === 'asc') {
      setCurrentSort('package_asc');
    } else if (direction === 'desc') {
      setCurrentSort('package_desc');
    } else {
      setCurrentSort(undefined);
    }
  };

  const getMatchCountSortingDirection = () => {
    if (currentSort === 'matchCount_asc') {
      return 'asc';
    }
    if (currentSort === 'matchCount_desc') {
      return 'desc';
    }
    return undefined;
  };

  const setMatchCountSortingDirection = (direction?: SortingDirection) => {
    if (direction === 'asc') {
      setCurrentSort('matchCount_asc');
    } else if (direction === 'desc') {
      setCurrentSort('matchCount_desc');
    } else {
      setCurrentSort(undefined);
    }
  };

  // Determine if the banner should be shown
  const hasSnippets = !!snippets?.results?.length;
  const shouldShowBanner = hasSnippets && (
    selectionState.selection === 'all' || (selectionState.selection.size === snippets?.results?.length)
  );

  const renderTableRows = () => {
    if (isLoading) {
      return _.times(queryParams.pageSize || 10, (i) => <LoaderRow key={i} />);
    }

    if (!snippets?.results?.length) {
      return <FallbackImage src="no-results" alt="No snippets found">No snippets found.</FallbackImage>;
    }

    return snippets?.results?.map((snippet, index) => (
      <div
        key={snippet.id}
        className={cx(styles.tableBodyRow, {
          [styles.tableBodyRowRejected]: !!snippet.rejectionDetails,
          [styles.tableBodyRowBorderTop]: index === 0 && shouldShowBanner,
          // We hardcode 10 (instead of using pageSize) because the table itself never gets
          // shorter than 10 rows high due to how the shared layout (with the file tree) is structured.
          [styles.tableBodyRowBorderBottom]: index === snippets.results.length - 1 && snippets.results.length < 10,
        })}
        onClick={() => setQuery({ ...queryParams, selectedSnippetId: snippet.id })}
      >
        <span>
          <Checkbox
            isChecked={selectionState.isSnippetSelected(snippet.id)}
            onChange={() => selectionState.toggleSnippet(snippet.id)}
            shouldStopPropagation
            isRippleDisabled
          />
        </span>
        <span>
          <IconAndNode icon={snippetKindIcons[snippet.kind]} marginAmount="0.5em">
            <span className={cx({ [styles.packageRejected]: !!snippet.rejectionDetails })}>{snippet.package}</span>
          </IconAndNode>
        </span>
        <span>{snippet.version}</span>
        <span><IssueCounts snippet={snippet} /></span>
        <span>{snippet.matchCount}</span>
        <span>
          <PackageLabelsList
            labels={snippet.labels}
            dependencyVersion={snippet.version}
            labelPrefix=""
          />
        </span>
      </div>
    ));
  };

  return (
    <List className={styles.table}>
      <div className={styles.tableHeaderRow}>
        <List.HeaderText>
          <Checkbox
            isChecked={selectionState.isHeaderChecked()}
            isIndeterminate={selectionState.isHeaderIndeterminate()}
            onChange={() => selectionState.toggleHeader()}
            isRippleDisabled
          />
        </List.HeaderText>
        <SortingDropdown
          direction={getPackageSortingDirection()}
          setDirection={setPackageSortingDirection}
        >
          <List.HeaderText>Package</List.HeaderText>
        </SortingDropdown>
        <List.HeaderText>Version</List.HeaderText>
        <List.HeaderText>Issues</List.HeaderText>
        <SortingDropdown
          direction={getMatchCountSortingDirection()}
          setDirection={setMatchCountSortingDirection}
        >
          <List.HeaderText>Matches</List.HeaderText>
        </SortingDropdown>
        <List.HeaderText>Labels</List.HeaderText>
      </div>
      {shouldShowBanner && !!snippets && (
        <BulkSelectionBanner
          selectionState={selectionState}
          snippets={snippets}
          userRevisionLocator={userRevisionLocator}
        />
      )}
      <List.Body>
        {renderTableRows()}
      </List.Body>
    </List>
  );
};

export default SnippetsTable;
